import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "./useAuth.js";
import { useAuthStore } from "./store.js";
import { useTenantContext } from "./TenantGate.js";
import type { Tenant } from "./types.js";
import { radius, typography } from "@aeriosiq/ui";
import { LoginCard } from "./LoginCard.js";
import { NetworkErrorRetryCard } from "./NetworkErrorRetryCard.js";
import { UnknownTenantAlert } from "./UnknownTenantAlert.js";
import { WrongTenantAlert } from "./WrongTenantAlert.js";
import { ForgotPasswordModal } from "./ForgotPasswordModal.js";

function isSafeReturnTo(value: string | null): value is string {
  return (
    typeof value === "string" &&
    value.startsWith("/") &&
    !value.startsWith("//")
  );
}

export function LoginPage() {
  const tenantQuery = useTenantContext();
  const status = useAuthStore((s) => s.status);
  const [params] = useSearchParams();
  const rawReturnTo = params.get("return_to");
  // WS-NAV — with workspace navigation there is no single "everyone's first
  // page" any more: an analyst and a pilot want different destinations. The
  // default lands on Home with the `landing` flag, which applies the two
  // landing rules (single-workspace users skip Home; returning users go to
  // their last-used workspace). An explicit `return_to` still wins, so a
  // deep-link sign-in is unaffected.
  const returnTo = isSafeReturnTo(rawReturnTo)
    ? rawReturnTo
    : "/home?landing=1";
  // SB5-A — invite acceptance redirects here with this flag set so we
  // can flash a success banner above the login form.
  const invitationAccepted = params.get("invitation_accepted") === "1";

  // 1. Loading state — UX §State 1.
  if (tenantQuery.isPending) {
    return (
      <LoginCard>
        <Skeleton
          variant="circular"
          width={72}
          height={72}
          sx={{ mx: "auto" }}
        />
        <Skeleton
          variant="text"
          width={220}
          sx={{ mx: "auto", fontSize: "1.5rem" }}
        />
        <Skeleton variant="text" width={180} sx={{ mx: "auto" }} />
        <Skeleton variant="rectangular" height={56} />
        <Skeleton variant="rectangular" height={44} />
      </LoginCard>
    );
  }

  // 5. Unknown-tenant state — UX §State 5 / AC8. No form, no button.
  if (tenantQuery.unknownTenant) {
    return <UnknownTenantAlert />;
  }

  // Network error (non-404) — UX §State 6 + Addendum 2026-05-04 (B-009.1).
  // The "Try again" button receives focus on first paint and re-fires
  // tenantQuery.refetch(); see NetworkErrorRetryCard for the full spec.
  if (tenantQuery.isError || !tenantQuery.tenant) {
    return <NetworkErrorRetryCard refetch={tenantQuery.refetch} />;
  }

  // 4. Wrong-tenant alert — UX §State 4 / AC6.
  if (status === "wrong_tenant") {
    return <WrongTenantAlert tenant={tenantQuery.tenant} />;
  }

  // 2. Default state — UX §State 2 / AC2 + AC3.
  return (
    <LoginForm
      tenant={tenantQuery.tenant}
      returnTo={returnTo}
      invitationAccepted={invitationAccepted}
    />
  );
}

function LoginForm({
  tenant,
  returnTo,
  invitationAccepted = false,
}: {
  tenant: Tenant;
  returnTo: string;
  invitationAccepted?: boolean;
}) {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);

  // Pre-fill the placeholder from the tenant slug — UX brief §State 2.
  const placeholder = `name@${tenant.tenantSlug}.com`;

  // useAuth().signIn redirects via window.location.assign; we don't expect to
  // unmount cleanly, but if we do (e.g. test harness), reset the spinner.
  useEffect(() => {
    return () => {
      setSubmitting(false);
    };
  }, []);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signIn({
        ...(email ? { loginHint: email } : {}),
        returnTo,
      });
    } catch (err) {
      setSubmitting(false);
      setError("Couldn't reach the sign-in service. Try again.");
      console.error("signIn failed", err);
    }
  }

  return (
    <LoginCard>
      <Stack spacing={3} alignItems="center">
        {tenant.brandingOverride?.logoUrl ? (
          <Box
            component="img"
            src={tenant.brandingOverride.logoUrl}
            alt={`${tenant.displayName} logo`}
            sx={{ height: 72, width: "auto" }}
          />
        ) : null}
        <Typography
          component="h1"
          id="login-title"
          textAlign="center"
          sx={{
            // Apple-clean marketing rhythm (CI_custom §10 step 5):
            // display.lg headline (56 px), semibold weight, tight
            // negative tracking. Single-statement hierarchy — the
            // tenant name *is* the hero.
            fontSize: {
              xs: typography.size["display-sm"],
              sm: typography.size["display-md"],
              md: typography.size["display-lg"],
            },
            fontWeight: typography.weight.semibold,
            letterSpacing: typography.tracking["display-lg"],
            lineHeight: 1.05,
          }}
        >
          {tenant.displayName}
        </Typography>
        <Typography
          color="text.secondary"
          textAlign="center"
          sx={{
            fontSize: typography.size["title-md"],
            letterSpacing: typography.tracking["title-md"],
          }}
        >
          Sign in to continue
        </Typography>
      </Stack>
      {invitationAccepted ? (
        <Alert
          severity="success"
          role="status"
          data-testid="login-invitation-accepted-banner"
        >
          Your account is ready — sign in to continue.
        </Alert>
      ) : null}
      {forgotOpen ? null : (
        <Box component="form" onSubmit={onSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              autoComplete="username"
              required
              fullWidth
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              placeholder={placeholder}
              disabled={submitting}
              slotProps={{
                htmlInput: { "aria-describedby": "login-email-hint" },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              color="primary"
              disabled={submitting}
              aria-busy={submitting}
              sx={{
                // Full-pill primary CTA — iOS marketing-style, 44 px tall.
                // CI_custom §5.1 nominates `radius.pill` (980 px) for
                // primary CTAs on the entry-marketing surfaces.
                minHeight: 44,
                borderRadius: `${String(radius.pill)}px`,
              }}
            >
              {submitting ? (
                <>
                  {/*
                  aria-hidden so the button's accessible name is the single
                  string "Signing in…" rather than a fused composite of a
                  nested role="progressbar" + the text label. The button's
                  own aria-busy="true" carries the live status to AT users.
                */}
                  <CircularProgress
                    size={20}
                    aria-hidden="true"
                    sx={{ mr: 1, color: "inherit" }}
                  />
                  Signing in&hellip;
                </>
              ) : (
                "Sign in"
              )}
            </Button>
            {error ? (
              <Typography role="alert" color="error" variant="body2">
                {error}
              </Typography>
            ) : null}
          </Stack>
        </Box>
      )}
      <Stack spacing={1} alignItems="center">
        {/* Story 052 AC18 — Forgot-password link. Rendered as <a> for
            screen-reader role:link semantics (Playwright's
            getByRole('link', { name: /forgot password/i }) finds it). */}
        <Box
          component="a"
          href="#forgot-password"
          role="link"
          aria-label="Forgot password?"
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            setForgotOpen(true);
          }}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontSize: "0.875rem",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: 2,
              borderRadius: 1,
            },
          }}
        >
          Forgot password?
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          id="login-email-hint"
          textAlign="center"
        >
          Need an account? Ask your tenant admin.
        </Typography>
      </Stack>
      <ForgotPasswordModal
        open={forgotOpen}
        onClose={() => {
          setForgotOpen(false);
        }}
      />
    </LoginCard>
  );
}
